<script setup>
import { ref, computed, watch } from 'vue'
import { store } from '@/store/appStore.js'

const warehouseFilter = ref('All'); const search = ref('')
const toast = ref(null)
const notify = msg => { toast.value = msg; setTimeout(() => { toast.value = null }, 3200) }

const warehouses = computed(() => ['All',...new Set(store.data.stock.value.map(s=>s.warehouse))].sort())
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return store.data.stock.value.filter(s => (warehouseFilter.value==='All'||s.warehouse===warehouseFilter.value) && (!q||s.productName.toLowerCase().includes(q)||s.productSku.toLowerCase().includes(q)||s.batchNo.toLowerCase().includes(q)||s.brandName.toLowerCase().includes(q)))
})
const oosCount = computed(() => store.data.stock.value.filter(s=>s.oosStatus).length)
const lowCount  = computed(() => store.data.stock.value.filter(s=>s.lowStock).length)

function barSegments(s) {
  const total = s.availableQty + s.reservedQty + s.inTransitQty
  if (!total) return { a:0, r:0, t:0 }
  return { a: Math.round(s.availableQty/total*100), r: Math.round(s.reservedQty/total*100), t: Math.round(s.inTransitQty/total*100) }
}

// Phase 3: land here from a global search result. Watcher, not onMounted —
// AppShell won't remount this component if the user searches again while
// already on Stock Dashboard.
watch(() => store.state.pendingFocus, f => {
  if (f && f.type === 'stock') {
    search.value = f.query || f.id
    store.clearPendingFocus()
  }
}, { immediate: true })
</script>
<template>
  <div>
    <div class="card module-header"><h2>{{ store.data.stock.value.length }} SKU × warehouse positions</h2></div>

    <div v-if="toast" style="position:fixed;bottom:24px;right:24px;background:var(--color-primary-dark);color:#fff;padding:10px 16px;border-radius:8px;font-size:13px;box-shadow:var(--shadow-lg);max-width:360px;z-index:120;">{{ toast }}</div>

    <div class="stat-row">
      <div class="card stat-card"><div class="stat-value">{{ store.data.stock.value.length }}</div><div class="stat-label">Total stock rows</div></div>
      <div class="card stat-card"><div class="stat-value" :style="oosCount?'color:var(--status-red)':''">{{ oosCount }}</div><div class="stat-label">Out of stock</div></div>
      <div class="card stat-card"><div class="stat-value" :style="lowCount?'color:var(--color-accent)':''">{{ lowCount }}</div><div class="stat-label">Nearing reorder</div></div>
    </div>

    <div class="card">
      <div class="toolbar" style="flex-wrap:wrap;">
        <div style="display:flex;gap:4px;">
          <button v-for="w in warehouses" :key="w" class="filter-select" :style="warehouseFilter===w?'background:var(--color-primary-dark);color:#fff;border-color:var(--color-primary-dark);':'cursor:pointer;'" @click="warehouseFilter=w">{{ w }}</button>
        </div>
        <input class="search-input" v-model="search" placeholder="Search product, SKU, brand or batch no.…" />
        <span class="result-count">{{ filtered.length }} of {{ store.data.stock.value.length }} shown</span>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Product</th><th>SKU</th><th>Warehouse</th><th>Batch</th><th>Expiry</th><th style="min-width:160px;">Available / Reserved / In-Transit</th><th>Flag</th></tr></thead>
          <tbody>
            <tr v-for="s in filtered" :key="s.productSku+s.warehouse">
              <td class="cell-strong">{{ s.productName }}</td><td class="cell-muted">{{ s.productSku }}</td><td>{{ s.warehouse }}</td>
              <td class="cell-muted">{{ s.batchNo }}</td><td class="cell-muted">{{ s.expiryDate }}</td>
              <td>
                <div style="display:flex;height:14px;border-radius:4px;overflow:hidden;background:var(--color-border);margin-bottom:4px;">
                  <div :style="{width: barSegments(s).a+'%', background: s.oosStatus?'var(--status-red)':'var(--status-green)'}" :title="'Available: '+s.availableQty"></div>
                  <div :style="{width: barSegments(s).r+'%', background: 'var(--color-accent)'}" :title="'Reserved: '+s.reservedQty"></div>
                  <div :style="{width: barSegments(s).t+'%', background: 'var(--color-primary-dark)'}" :title="'In-Transit: '+s.inTransitQty"></div>
                </div>
                <span class="cell-muted" style="font-size:11px;">{{ s.availableQty }} / {{ s.reservedQty }} / {{ s.inTransitQty }}</span>
              </td>
              <td>
                <span v-if="s.oosStatus" class="tier-badge" style="background:var(--status-red-bg);color:var(--status-red);">OOS</span>
                <span v-else-if="s.lowStock" class="tier-badge" style="background:var(--status-yellow-bg);color:#97731f;">Low</span>
                <span v-else class="tier-badge" style="background:var(--status-green-bg);color:var(--status-green);">Healthy</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>